/**
 * Zod schemas for the Refine admin Users list + update surfaces (API-1567,
 * API-1571).
 *
 * Backs `GET /api/v1/admin/users` — a paginated, sortable, filterable list of
 * users for the admin control center — and `PATCH /api/v1/admin/users/:id`,
 * which updates the backed, editable fields (`name`, `emailVerified`). Role
 * changes stay on the legacy `admin.ts` `users:manage` route
 * (`PATCH /users/:userId/role`) — that endpoint owns the self-demotion +
 * last-SUPER_ADMIN guards, so this update surface deliberately never touches
 * `role`.
 *
 * Error envelope: the route handler reshapes Zod failures into the canonical
 * `{ error, code, message }` envelope. Do NOT route these through
 * `createErrorResponse()` from `errors.ts` — that emits the
 * `{ details, timestamp, requestId }` envelope which the Refine client does
 * not expect.
 *
 * Honest-derivation note: several display fields are DERIVED, not stored:
 *   - `status` is computed from `emailVerified` + recent `Session` activity.
 *   - `karma` has no backing column today — the API always returns `null`.
 *   - `reportsCount` has no user-report model today — the API returns `0`.
 * These are intentionally never fabricated; see the route handler.
 */
import { z } from 'zod';

/**
 * Role values the admin list can filter / display. Mirrors the Prisma `Role`
 * enum in `packages/db-core/prisma/schema.prisma`. Kept in sync manually —
 * the validation package cannot import db-core's generated client without
 * pulling Prisma into every consumer.
 */
export const adminUserRoleSchema = z.enum(['USER', 'MODERATOR', 'ADMIN', 'SUPER_ADMIN']);
export type AdminUserRole = z.infer<typeof adminUserRoleSchema>;

/**
 * Derived lifecycle status for the admin Users list status tabs.
 *
 *   - `active`    — email verified AND has a Session active within 30 days.
 *   - `pending`   — email NOT verified (account never confirmed).
 *   - `dormant`   — email verified BUT no Session activity in 30 days.
 *   - `suspended` — NO backing column today. Selecting it returns an empty
 *                   set (`{ data: [], total: 0 }`) rather than 400 so the UI
 *                   tab renders without error until a real column lands.
 *   - `banned`    — same as `suspended`: no backing column → empty set.
 *
 * The first three are real, computable bands. The last two are honest
 * placeholders for tabs the UI exposes but the schema cannot yet satisfy.
 */
export const adminUserStatusFilterSchema = z.enum([
  'active',
  'pending',
  'dormant',
  'suspended',
  'banned',
]);
export type AdminUserStatusFilter = z.infer<typeof adminUserStatusFilterSchema>;

/**
 * Derived status values that actually appear on a returned `AdminUserRow`.
 * Only the three computable bands — `suspended`/`banned` are never assigned
 * to a row because no column backs them.
 */
export const adminUserDerivedStatusSchema = z.enum(['active', 'pending', 'dormant']);
export type AdminUserDerivedStatus = z.infer<typeof adminUserDerivedStatusSchema>;

/**
 * Allow-listed sort fields for the users list. Each maps to a real,
 * sortable column in the route's raw SQL — never a derived field that
 * would require a post-query sort.
 */
export const adminUsersListSortFieldSchema = z.enum([
  'email',
  'name',
  'role',
  'createdAt',
  'lastActiveAt',
]);
export const adminUsersListSortDirectionSchema = z.enum(['asc', 'desc']);

export const ADMIN_USERS_LIST_DEFAULT_PAGE_SIZE = 20;
export const ADMIN_USERS_LIST_MAX_PAGE_SIZE = 200;

/**
 * Query schema for `GET /api/v1/admin/users`.
 *
 * Strict bounds protect the downstream raw SQL from `take` blowups and
 * unbounded filter strings. `.strict()` rejects unknown params so a typoed
 * filter key surfaces a 400 instead of being silently ignored (mirrors the
 * events-list contract).
 */
export const adminUsersListQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce
      .number()
      .int()
      .min(1)
      .max(ADMIN_USERS_LIST_MAX_PAGE_SIZE)
      .default(ADMIN_USERS_LIST_DEFAULT_PAGE_SIZE),
    /**
     * Format: `<field>:<direction>` (e.g. `createdAt:desc`). Default is
     * `createdAt:desc` — operators usually want the newest signups first.
     */
    sort: z
      .string()
      .trim()
      .regex(/^[a-zA-Z]+:(asc|desc)$/, "Sort must be '<field>:<asc|desc>'")
      // Validate the field portion against the allow-list so an off-list field
      // (e.g. `karma:asc`) returns 400 instead of silently falling back to the
      // default sort — keeps the contract consistent with `.strict()` rejecting
      // typo'd keys. `parseAdminUsersListSort` re-parses the validated value.
      .refine((v) => adminUsersListSortFieldSchema.safeParse(v.split(':')[0]).success, {
        message:
          "Sort field must be one of: email, name, role, createdAt, lastActiveAt (format '<field>:<asc|desc>')",
      })
      .optional(),
    /**
     * Free-text filter applied to name, email, AND id (ILIKE `%q%` on the
     * first two, exact-or-ILIKE on id). Trimmed; empty rejected.
     */
    q: z.string().trim().min(1).max(200).optional(),
    /** Lifecycle status tab. See `adminUserStatusFilterSchema`. */
    status: adminUserStatusFilterSchema.optional(),
    /** Exact role filter. */
    role: adminUserRoleSchema.optional(),
    /**
     * Chip: only users who have at least one report against them. No
     * user-report model exists today, so this chip can never match a row
     * (every user has `reportsCount = 0`); selecting it returns an empty
     * set rather than 400. Boolean accepted as `'true'`/`'false'`/bool.
     */
    hasReports: z
      .union([z.literal('true'), z.literal('false'), z.boolean()])
      .transform((v) => v === true || v === 'true')
      .optional(),
    /**
     * Chip: only users whose email is NOT verified (`emailVerified = false`).
     * Equivalent to the `pending` status band; surfaced as a standalone chip
     * because the UI lets operators stack it with other filters.
     */
    unverified: z
      .union([z.literal('true'), z.literal('false'), z.boolean()])
      .transform((v) => v === true || v === 'true')
      .optional(),
    /**
     * Chip: only users who have authored at least one event (an "organizer").
     * Backed by the `events.userId` FK — a user with any owned event row.
     */
    organizer: z
      .union([z.literal('true'), z.literal('false'), z.boolean()])
      .transform((v) => v === true || v === 'true')
      .optional(),
    /**
     * Chip / facet: filter by the user's primary city. Matched against the
     * first entry of `UserPreferenceProfile.preferredCities`. Trimmed; empty
     * rejected.
     */
    city: z.string().trim().min(1).max(120).optional(),
  })
  .strict();
export type AdminUsersListQuery = z.infer<typeof adminUsersListQuerySchema>;

/** Resolved sort tuple after parsing the `sort` query param. */
export interface AdminUsersListSort {
  field: z.infer<typeof adminUsersListSortFieldSchema>;
  direction: z.infer<typeof adminUsersListSortDirectionSchema>;
}

/**
 * Parse a `sort=<field>:<direction>` string into a typed tuple. Returns
 * `null` when the sort key is not on the allow-list (the route handler
 * treats `null` as "fall back to the default sort"). Mirrors
 * `parseAdminEventsListSort`.
 */
export function parseAdminUsersListSort(raw: string | undefined): AdminUsersListSort | null {
  if (!raw) return null;
  // Reject malformed inputs with extra separators (e.g. `name:asc:extra`) —
  // destructuring alone would silently ignore the trailing segment.
  const parts = raw.split(':');
  if (parts.length !== 2) return null;
  const [rawField, rawDirection] = parts;
  const fieldResult = adminUsersListSortFieldSchema.safeParse(rawField);
  const directionResult = adminUsersListSortDirectionSchema.safeParse(rawDirection);
  if (!fieldResult.success || !directionResult.success) {
    return null;
  }
  return { field: fieldResult.data, direction: directionResult.data };
}

/**
 * ISO 8601 datetime in UTC. Mirrors `admin/event.ts`'s (and `admin/venue.ts`'s)
 * `updatedAt` concurrency-token validator — duplicated per-file per this
 * package's established per-resource convention (each `admin/*.ts` schema
 * file is self-contained).
 */
const isoDateTimeSchema = z
  .string()
  .trim()
  .min(1)
  .refine((value) => {
    const ms = Date.parse(value);
    return Number.isFinite(ms);
  }, 'Must be an ISO 8601 datetime string');

/**
 * Body for `PATCH /api/v1/admin/users/:id` (API-1571).
 *
 * Only the BACKED, editable fields reachable from the admin User edit form:
 *   - `name`: `User.name` — nullable; sending `null` clears it.
 *   - `emailVerified`: `User.emailVerified`.
 *
 * Deliberately excluded:
 *   - `role` — stays on the legacy `PATCH /users/:userId/role` route, which
 *     owns the self-demotion + last-SUPER_ADMIN guards. This endpoint must
 *     NOT be able to change role.
 *   - `status` / `karma` — no backing column exists (see `AdminUserRow`'s
 *     doc comment); there is nothing to write.
 *
 * `updatedAt` is REQUIRED — the optimistic-concurrency token. The route
 * handler compares it against the persisted `User.updatedAt` and returns
 * `409 CONFLICT` on mismatch, mirroring `adminUpdateEventSchema` /
 * `adminUpdateVenueSchema`.
 */
export const adminUpdateUserSchema = z
  .object({
    name: z.string().trim().min(1).max(200).nullable().optional(),
    emailVerified: z.boolean().optional(),
    /**
     * Optimistic-concurrency token — required. Must equal
     * `currentRow.updatedAt.toISOString()` for the update to apply.
     */
    updatedAt: isoDateTimeSchema,
  })
  .strict();
export type AdminUpdateUserBody = z.infer<typeof adminUpdateUserSchema>;

/**
 * Shapes for the Refine admin Users summary surface (API-1568).
 *
 * Backs `GET /api/v1/admin/users/summary` — KPI tiles, a 14-day signup
 * sparkline, and a "recently joined" feed for the admin users dashboard.
 * No query params (no schema needed); these are response contracts only.
 *
 * Every value is DERIVED from `User`/`Session` — never fabricated, zeros
 * render as 0:
 *   - `dau` is the distinct count of `Session.userId` active today.
 *   - `awaitingReview` = pending (email-unverified) users.
 *   - `staff` = users whose role ∈ {MODERATOR, ADMIN, SUPER_ADMIN}.
 * Soft-deleted users (`accountDeletionRequestedAt` set) are excluded
 * everywhere, mirroring the list endpoint.
 */

/**
 * One daily bucket of the 14-day signup sparkline. `date` is the calendar
 * day (`YYYY-MM-DD`, UTC); `count` is the number of `User.createdAt` rows
 * that fall on it (zero-filled, never omitted).
 */
export interface AdminUsersSummarySignupBucket {
  date: string;
  count: number;
}

/**
 * A "recently joined" feed entry — the last-24h signups, newest first,
 * capped at 6. `status` reuses the same `AdminUserDerivedStatus` band as
 * the list endpoint.
 */
export interface AdminUsersSummaryRecentlyJoined {
  id: string;
  name: string | null;
  primaryCity: string | null;
  createdAt: string;
  status: AdminUserDerivedStatus;
}

/**
 * Payload returned by `GET /api/v1/admin/users/summary` (wrapped as
 * `{ data: AdminUsersSummary }` by the handler).
 */
export interface AdminUsersSummary {
  /** All non-soft-deleted users. */
  totalUsers: number;
  /** Distinct users with a session active today (daily active users). */
  dau: number;
  /** Users created since the start of today (UTC). */
  newToday: number;
  /** Pending (email-unverified) users awaiting review. */
  awaitingReview: number;
  /** Users with a staff role (MODERATOR / ADMIN / SUPER_ADMIN). */
  staff: number;
  /** 14 daily createdAt buckets, oldest→newest, zero-filled. */
  signups14d: AdminUsersSummarySignupBucket[];
  /** Last-24h signups, newest first, capped at 6. */
  recentlyJoined: AdminUsersSummaryRecentlyJoined[];
}
